import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BarWithUser } from "@shared/schema";
import { Link, useLocation, useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, PenLine, Flame, Sparkles, Trophy, Hash, X, Compass, Heart, ChevronRight, TrendingUp } from "lucide-react";
import { useBars } from "@/context/BarContext";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PullToRefresh } from "@/components/PullToRefresh";
import { BarSkeletonList } from "@/components/BarSkeleton";
import { SearchBar } from "@/components/SearchBar";
import BarCard from "@/components/BarCard";
import ActivityStrip from "@/components/ActivityStrip";
import CommunitySpotlight from "@/components/CommunitySpotlight";
import { QuickStats } from "@/components/QuickStats";
import { cn } from "@/lib/utils";
import {
  NativeGlassCard,
  NativeScreen,
  NativeSegmentedControl,
} from "@/components/ui/native-shell";
import { useBackground } from "@/components/BackgroundSelector";
import {
  BarOfTheDay,
  TrendingTags,
  RecentActivityFeed,
  SidebarPrompt,
  XpProgressCard,
  CommunityPulse,
  RecentChallenges,
  SuggestedWriters,
} from "@/components/SidebarWidgets";

type FeedTab = "latest" | "top" | "trending" | "challenges";
const FEED_TAB_OPTIONS: Array<{ value: FeedTab; label: string }> = [
  { value: "latest", label: "Latest" },
  { value: "top", label: "Top" },
  { value: "trending", label: "Trending" },
  { value: "challenges", label: "Challenges" },
];

function formatCompact(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

// Mobile-only horizontal trending tags
function MobileTrendingTags({ hasCustomBackground }: { hasCustomBackground: boolean }) {
  const [, setLocation] = useLocation();
  const { data: tags = [] } = useQuery({
    queryKey: ["sidebar-trending-tags"],
    queryFn: () => api.getTrendingTags(),
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  if (tags.length === 0) return null;

  return (
    <section className="md:hidden">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="h-4 w-4 text-emerald-400" />
        <p className="text-sm font-semibold">Trending Tags</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 snap-x scrollbar-hide -mx-4 px-4">
        {tags.map((t: { tag: string; usage_count: number }) => (
          <button
            key={t.tag}
            onClick={() => setLocation(`/?tag=${encodeURIComponent(t.tag)}`)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium snap-start flex-shrink-0 transition-colors",
              hasCustomBackground 
                ? "glass-surface-strong border border-primary/20 text-primary hover:bg-primary/20"
                : "bg-primary/10 text-primary hover:bg-primary/20"
            )}
          >
            <Hash className="h-3 w-3" />
            {t.tag}
            <span className="text-[10px] opacity-60">{t.usage_count}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { bars, isLoadingBars, refetchBars, currentUser } = useBars();
  const { selectedBackground } = useBackground();
  const [activeTab, setActiveTab] = useState<FeedTab>("latest");
  const [isSearchVisible, setIsSearchVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const isScrollTickingRef = useRef(false);
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const tagFilter = params.get("tag");
  const { toast } = useToast();

  // Determine if we should apply frosted glass effect
  const hasCustomBackground = selectedBackground.id !== 'default' && selectedBackground.image;

  useEffect(() => {
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    if (!error) {
      return;
    }

    const message = errorDescription
      ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
      : "Authentication failed. Please try again.";

    toast({
      title: "Sign In Failed",
      description: message,
      variant: "destructive",
    });

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [params, toast]);

  const { data: communityStats } = useQuery({
    queryKey: ["community-stats"],
    queryFn: () => api.getCommunityStats(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: activityItems = [] } = useQuery({
    queryKey: ["community-now"],
    queryFn: () => api.getNowActivity(8),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: spotlight } = useQuery({
    queryKey: ["community-spotlight"],
    queryFn: () => api.getCommunitySpotlight(),
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  const { data: currentPrompt } = useQuery({
    queryKey: ["current-prompt"],
    queryFn: () => api.getCurrentPrompt(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: leaderboard = [] } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: async () => {
      const res = await fetch("/api/leaderboard?limit=8", { credentials: "include" });
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: topBars = [], isLoading: isTopLoading } = useQuery({
    queryKey: ["bars-top"],
    queryFn: async () => {
      const res = await fetch("/api/bars/feed/top", { credentials: "include" });
      return res.json();
    },
    enabled: !tagFilter && activeTab === "top",
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: trendingBars = [], isLoading: isTrendingLoading } = useQuery({
    queryKey: ["bars-trending"],
    queryFn: async () => {
      const res = await fetch("/api/bars/feed/trending", { credentials: "include" });
      return res.json();
    },
    enabled: !tagFilter && activeTab === "trending",
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: challengeBars = [], isLoading: isChallengesLoading } = useQuery({
    queryKey: ["bars-challenges"],
    queryFn: () => api.getChallenges(30),
    enabled: !tagFilter && activeTab === "challenges",
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: tagBars = [], isLoading: isTagBarsLoading } = useQuery({
    queryKey: ["bars-by-tag", tagFilter],
    queryFn: async () => {
      if (!tagFilter) return [];
      const res = await fetch(`/api/bars/by-tag/${encodeURIComponent(tagFilter)}`, {
        credentials: "include",
      });
      return res.json();
    },
    enabled: !!tagFilter,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: messageOfTheDay } = useQuery({
    queryKey: ["message-of-the-day"],
    queryFn: async () => {
      const res = await fetch("/api/message-of-the-day");
      return res.json();
    },
    staleTime: 300_000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const visibleBars = useMemo<BarWithUser[]>(() => {
    if (tagFilter) return tagBars;
    if (activeTab === "top") return topBars;
    if (activeTab === "trending") return trendingBars;
    if (activeTab === "challenges") return challengeBars as BarWithUser[];
    return bars;
  }, [tagFilter, tagBars, activeTab, topBars, trendingBars, challengeBars, bars]);

  const isLoading = tagFilter
    ? isTagBarsLoading
    : activeTab === "top"
      ? isTopLoading
      : activeTab === "trending"
        ? isTrendingLoading
        : activeTab === "challenges"
          ? isChallengesLoading
          : isLoadingBars;

  const handleRefresh = useCallback(async () => {
    await refetchBars();
    queryClient.invalidateQueries({ queryKey: ["bars-top"] });
    queryClient.invalidateQueries({ queryKey: ["bars-trending"] });
    queryClient.invalidateQueries({ queryKey: ["bars-challenges"] });
    queryClient.invalidateQueries({ queryKey: ["bars-by-tag", tagFilter] });
    queryClient.invalidateQueries({ queryKey: ["community-now"] });
    queryClient.invalidateQueries({ queryKey: ["community-spotlight"] });
    queryClient.invalidateQueries({ queryKey: ["community-stats"] });
  }, [queryClient, refetchBars, tagFilter]);

  const stats = {
    totalBars: communityStats?.totalBars ?? bars.length,
    barsThisWeek: communityStats?.barsThisWeek ?? 0,
    activeWritersMonth: communityStats?.activeWritersMonth ?? 0,
  };

  const showFeed = () => {
    const feed = document.getElementById("feed-start");
    if (feed) {
      feed.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const clearTagFilter = () => setLocation("/");

  useEffect(() => {
    const SCROLL_DELTA_THRESHOLD = 10;

    const handleScroll = () => {
      if (window.innerWidth >= 768 || isScrollTickingRef.current) return;

      isScrollTickingRef.current = true;
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;

        if (currentScrollY <= 8) {
          setIsSearchVisible(true);
          lastScrollYRef.current = currentScrollY;
          isScrollTickingRef.current = false;
          return;
        }

        const delta = currentScrollY - lastScrollYRef.current;
        if (Math.abs(delta) >= SCROLL_DELTA_THRESHOLD) {
          if (delta > 0) setIsSearchVisible(false);
          else setIsSearchVisible(true);
          lastScrollYRef.current = currentScrollY;
        }

        isScrollTickingRef.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <NativeScreen
      className="pt-14 pb-[calc(env(safe-area-inset-bottom)+6.5rem)] md:pt-20 md:pb-8"
      contentClassName="px-4 md:px-6"
    >
        <div
          className={cn(
            "md:hidden fixed mobile-search-offset left-3 right-3 z-[1190] transition-all duration-200 ease-out",
            isSearchVisible ? "translate-y-0 opacity-100" : "-translate-y-[130%] opacity-0 pointer-events-none",
          )}
        >
          <div className="glass-surface rounded-2xl p-3">
            <SearchBar />
          </div>
        </div>

        <div className="grid gap-6 w-full lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px]">
          <section className="space-y-5 w-full min-w-0">
            <div
              className={cn(
                "md:hidden transition-[height] duration-200 ease-out",
                isSearchVisible ? "mobile-search-spacer" : "h-0",
              )}
              aria-hidden
            />

            <NativeGlassCard animate className={cn(
              "relative overflow-hidden border-foreground/[0.08] p-6 md:p-8",
              hasCustomBackground && "glass-surface-strong border-foreground/[0.15]"
            )}>
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
              <div className="absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none" />

              <div className="relative">
                <Badge className="mb-3 bg-primary/15 text-primary hover:bg-primary/20">
                  Modern writer room
                </Badge>
                <h1 className="max-w-3xl text-[clamp(2rem,4.4vw,3.4rem)] font-black leading-tight tracking-tight">
                  {messageOfTheDay?.message || "Drop the lines that do not fit anywhere else."}
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
                  Orphan Bars is a focused place to share loose bars, discover sharp writing,
                  and keep the culture moving through reactions and responses.
                </p>

                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => setLocation("/post")}
                    data-testid="button-hero-write"
                  >
                    Write a bar
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={showFeed}
                    data-testid="button-hero-browse"
                  >
                    Browse latest
                  </Button>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-border/60 bg-background/60 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Total bars
                    </p>
                    <p className="mt-1 text-2xl font-bold text-primary">
                      {formatCompact(stats.totalBars)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-background/60 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Bars this week
                    </p>
                    <p className="mt-1 text-2xl font-bold text-primary">
                      {formatCompact(stats.barsThisWeek)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-background/60 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Active writers (month)
                    </p>
                    <p className="mt-1 text-2xl font-bold text-primary">
                      {formatCompact(stats.activeWritersMonth)}
                    </p>
                  </div>
                </div>
              </div>
            </NativeGlassCard>

            <ActivityStrip items={activityItems} />
            <CommunitySpotlight spotlight={spotlight ?? null} />

            {/* Orphanage Integration */}
            <section className={cn(
              "rounded-2xl border p-5 glass-surface",
              hasCustomBackground 
                ? "border-primary/40 bg-primary/16 glass-surface-strong" 
                : "border-primary/30 bg-primary/10"
            )}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2">The Orphanage</h3>
                  <p className="text-sm text-muted-foreground">
                    Discover open-adopt bars cleared for commercial use
                  </p>
                </div>
                <Link href="/orphanage">
                  <Button 
                    variant="outline" 
                    className="border-primary/30 hover:border-primary hover:bg-primary/10"
                  >
                    Explore Orphanage
                  </Button>
                </Link>
              </div>
            </section>

            {currentPrompt && (
              <section className={cn(
                "rounded-2xl border p-5 glass-surface",
                hasCustomBackground 
                  ? "border-primary/40 bg-primary/16 glass-surface-strong" 
                  : "border-primary/30 bg-primary/10"
              )}>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary/80">
                  Current prompt
                </p>
                <p className="mt-2 text-xl font-semibold">{currentPrompt.text}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    onClick={() => setLocation(`/post?prompt=${currentPrompt.slug}`)}
                    data-testid="button-write-prompt"
                  >
                    Write to this prompt
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setLocation(`/prompts/${currentPrompt.slug}`)}
                  >
                    View prompt page
                  </Button>
                </div>
              </section>
            )}

            {/* How it works - horizontal scroll on mobile */}
            <section className={cn(
              "rounded-2xl border p-4 md:p-5 glass-surface",
              hasCustomBackground 
                ? "border-foreground/20 bg-white/5 glass-surface-strong" 
                : "border-border/60 bg-background/45"
            )}>
              <p className="mb-3 text-sm font-semibold">How it works</p>
              <div className="flex gap-3 overflow-x-auto pb-2 md:pb-0 md:grid md:grid-cols-3 snap-x snap-mandatory scrollbar-hide -mx-1 px-1">
                <div className={cn(
                  "rounded-xl border border-foreground/10 bg-white/5 p-3 min-w-[200px] md:min-w-0 snap-start flex-shrink-0 md:flex-shrink"
                )}>
                  <div className="mb-2 inline-flex rounded-full bg-primary/12 p-2 text-primary">
                    <Search className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold">1. Browse orphan bars.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Explore what writers are dropping right now.
                  </p>
                </div>
                <div className={cn(
                  "rounded-xl border border-foreground/10 bg-white/5 p-3 min-w-[200px] md:min-w-0 snap-start flex-shrink-0 md:flex-shrink"
                )}>
                  <div className="mb-2 inline-flex rounded-full bg-primary/12 p-2 text-primary">
                    <PenLine className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold">2. Drop your own lines.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Post one-liners, snippets, or challenge bars.
                  </p>
                </div>
                <div className={cn(
                  "rounded-xl border border-foreground/10 bg-white/5 p-3 min-w-[200px] md:min-w-0 snap-start flex-shrink-0 md:flex-shrink"
                )}>
                  <div className="mb-2 inline-flex rounded-full bg-primary/12 p-2 text-primary">
                    <Flame className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold">3. React and respond.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Give quick feedback and jump into call-and-response threads.
                  </p>
                </div>
              </div>
            </section>

            {/* Hot Right Now - horizontal mini-carousel on mobile */}
            {trendingBars.length > 0 && !tagFilter && (
              <section className="md:hidden">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-400" />
                    Hot Right Now
                  </p>
                  <button 
                    onClick={() => setActiveTab("trending")}
                    className="text-xs text-primary flex items-center gap-0.5 hover:underline"
                  >
                    See all <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                  {trendingBars.slice(0, 6).map((bar) => (
                    <Link key={bar.id} href={`/bar/${bar.id}`}>
                      <div className={cn(
                        "min-w-[260px] rounded-xl p-3 snap-start flex-shrink-0 border",
                        hasCustomBackground 
                          ? "glass-surface-strong border-foreground/15" 
                          : "glass-surface border-border/60"
                      )}>
                        <p className="text-sm line-clamp-2 mb-2 leading-relaxed">
                          "{bar.content?.slice(0, 80)}{bar.content?.length > 80 ? '...' : ''}"
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center text-[10px] overflow-hidden">
                            {bar.user?.avatarUrl ? (
                              <img src={bar.user.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              bar.user?.username?.[0]?.toUpperCase() || "?"
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground truncate">@{bar.user?.username}</span>
                          <span className="ml-auto flex items-center gap-1 text-[11px] text-rose-400">
                            <Heart className="h-3 w-3" /> {bar.likeCount || 0}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Mobile trending tags strip */}
            {!tagFilter && (
              <MobileTrendingTags hasCustomBackground={hasCustomBackground} />
            )}

            {/* Mobile featured writers horizontal */}
            {!tagFilter && leaderboard.length > 0 && (
              <section className="md:hidden">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <p className="text-sm font-semibold">Top Writers</p>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x scrollbar-hide -mx-4 px-4">
                  {leaderboard.slice(0, 8).map((user: any, index: number) => (
                    <Link key={user.id} href={`/u/${user.username}`}>
                      <div className={cn(
                        "flex flex-col items-center gap-1.5 min-w-[80px] rounded-xl p-3 snap-start flex-shrink-0",
                        hasCustomBackground 
                          ? "glass-surface-strong border border-foreground/10" 
                          : "glass-surface"
                      )}>
                        <div className="relative">
                          <div className="h-12 w-12 rounded-full bg-primary/15 overflow-hidden ring-2 ring-primary/20">
                            {user.avatarUrl ? (
                              <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-sm font-medium">
                                {user.username?.[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          {index < 3 && (
                            <div className={cn(
                              "absolute -bottom-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                              index === 0 && "bg-yellow-500 text-black",
                              index === 1 && "bg-gray-400 text-black",
                              index === 2 && "bg-amber-600 text-white"
                            )}>
                              {index + 1}
                            </div>
                          )}
                        </div>
                        <span className="text-[11px] text-foreground/80 truncate max-w-[70px]">@{user.username}</span>
                        <span className="text-[10px] text-primary font-medium">{formatCompact(user.xp || 0)} XP</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {tagFilter && (
              <div className={cn(
                "rounded-xl p-3 flex items-center gap-2",
                hasCustomBackground 
                  ? "border-foreground/20 bg-white/5 glass-surface-strong" 
                  : "border-border/60 bg-card/60"
              )}>
                <Hash className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Filtering by</span>
                <Badge variant="secondary">#{tagFilter}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={clearTagFilter}
                  data-testid="button-clear-tag"
                >
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              </div>
            )}

            {!tagFilter && (
              <div className="flex flex-col md:flex-row gap-3">
                <NativeGlassCard className="flex-1 p-2">
                  <NativeSegmentedControl
                    value={activeTab}
                    onChange={(tab) => setActiveTab(tab)}
                    options={FEED_TAB_OPTIONS}
                    className="w-full"
                  />
                </NativeGlassCard>
                <QuickStats className="md:w-80" />
              </div>
            )}

            <PullToRefresh onRefresh={handleRefresh}>
              <div id="feed-start" className="space-y-4 pb-4">
                {isLoading ? (
                  <BarSkeletonList count={5} />
                ) : visibleBars.length === 0 ? (
                  <div className={cn(
                    "rounded-2xl border border-dashed p-10 text-center",
                    hasCustomBackground 
                      ? "border-foreground/20 bg-white/5" 
                      : "border-border/70 bg-card/40"
                  )}>
                    <p className="text-base font-medium">
                      {tagFilter
                        ? `No bars found for #${tagFilter} yet.`
                        : activeTab === "challenges"
                          ? "No active challenges yet."
                          : "No bars yet."}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Start the energy with your first post.
                    </p>
                    <Button
                      className="mt-4"
                      onClick={() => setLocation("/post")}
                      data-testid="button-empty-state-post"
                    >
                      Drop your first orphan bar
                    </Button>
                  </div>
                ) : (
                  <>
                    {visibleBars.map((bar, index) => (
                      <div key={bar.id}>
                        {/* Visual section break every 5 items on mobile for visual rhythm */}
                        {index > 0 && index % 5 === 0 && (
                          <div className="md:hidden my-6 flex items-center gap-3">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                            <Sparkles className="h-4 w-4 text-primary/50" />
                            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-primary/30 to-transparent" />
                          </div>
                        )}
                        <div className="mobile-bar-enter">
                          <BarCard bar={bar} />
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </PullToRefresh>
          </section>

          <aside className="hidden lg:block">
            <div className="sticky top-20 space-y-4 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-hide pb-4">
              {/* XP Progress (logged-in users) */}
              <XpProgressCard hasCustomBackground={hasCustomBackground} />

              {/* Top lyricists leaderboard */}
              <div className={cn(
                "rounded-2xl p-4",
                hasCustomBackground 
                  ? "glass-surface-strong" 
                  : "NativeGlassCard"
              )}>
                <p className="mb-3 text-sm font-semibold flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  Top lyricists
                </p>
                <div className="space-y-2">
                  {leaderboard.length === 0 && (
                    <p className="text-xs text-muted-foreground">No ranking yet.</p>
                  )}
                  {leaderboard.slice(0, 6).map((user: any, index: number) => (
                    <Link key={user.id} href={`/u/${user.username}`}>
                      <a className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors">
                        <span className="w-5 text-xs text-muted-foreground">
                          {index + 1}
                        </span>
                        <div className="h-7 w-7 rounded-full bg-primary/15 flex items-center justify-center text-xs text-primary overflow-hidden">
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            user.username?.[0]?.toUpperCase()
                          )}
                        </div>
                        <span className="min-w-0 flex-1 truncate text-sm">
                          @{user.username}
                        </span>
                        <span className="text-xs text-primary">
                          {formatCompact(user.xp || 0)}
                        </span>
                      </a>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bar of the Day */}
              <BarOfTheDay hasCustomBackground={hasCustomBackground} />

              {/* This week's prompt */}
              <SidebarPrompt hasCustomBackground={hasCustomBackground} />

              {/* Trending tags */}
              <TrendingTags hasCustomBackground={hasCustomBackground} />

              {/* Community Pulse */}
              <CommunityPulse hasCustomBackground={hasCustomBackground} />

              {/* Live Activity */}
              <RecentActivityFeed hasCustomBackground={hasCustomBackground} />

              {/* Active Challenges */}
              <RecentChallenges hasCustomBackground={hasCustomBackground} />

              {/* Writers to Follow */}
              <SuggestedWriters hasCustomBackground={hasCustomBackground} />

              {/* Explore links */}
              <div className={cn(
                "rounded-2xl p-4",
                hasCustomBackground 
                  ? "glass-surface-strong" 
                  : "NativeGlassCard"
              )}>
                <p className="mb-2 text-sm font-semibold flex items-center gap-2">
                  <Compass className="h-4 w-4 text-primary" />
                  Explore
                </p>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setLocation("/prompts")}
                  >
                    Prompts
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setLocation("/challenges")}
                  >
                    Challenges
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setLocation(currentUser ? "/profile" : "/auth")}
                  >
                    My Bars
                  </Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
    </NativeScreen>
  );
}
